import React, { useState, useEffect } from 'react';
import { Button, CircularProgress, Tab, Tabs, Dialog, DialogTitle, DialogContent, DialogActions, TextField} from '@mui/material';
import { toast } from 'react-toastify';
import MuiTable from '../../../common-utilities/core/mui-table';
import { INVOICE_TRACKING_HOSTNAME } from '../../../common-utilities/utils/end-points';
import { VENDOR_ONBOARD_REST_URLS } from '../../../common-utilities/utils/end-points/vendor-onboard';
import { HTTP_METHODS, invokeApi } from '../../utils/http-service';

function TabPanel({ children, value, index }) {
  return <div hidden={value !== index}>{value === index && children}</div>;
}

const formatDate = (ts) => (ts ? new Date(ts).toLocaleString() : '-');

const VendorIntegrations = () => {
  const [subTab, setSubTab] = useState(0);
  const [successLogs, setSuccessLogs] = useState({});
  const [failedLogs, setFailedLogs] = useState({});
  const [successFilters, setSuccessFilters] = useState({ limit: 10, page: 1 });
  const [failedFilters, setFailedFilters] = useState({ limit: 10, page: 1 });
  const [retryState, setRetryState] = useState({});
  const [manualCodeModal, setManualCodeModal] = useState({ open: false, logId: null });
  const [manualCode, setManualCode] = useState('');

  const fetchSuccessLogs = (filters) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INVOICE_TRACKING_HOSTNAME}${VENDOR_ONBOARD_REST_URLS.BC_LOGS}`,
      null,
      { ...filters, status: 'SUCCESS' }
    )
      .then((res) => setSuccessLogs(res))
      .catch(() => {});
  };

  const fetchFailedLogs = (filters) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INVOICE_TRACKING_HOSTNAME}${VENDOR_ONBOARD_REST_URLS.BC_LOGS}`,
      null,
      { ...filters, status: 'FAILED' }
    )
      .then((res) => setFailedLogs(res))
      .catch(() => {});
  };

  useEffect(() => {
    fetchSuccessLogs(successFilters);
  }, []);

  useEffect(() => {
    fetchFailedLogs(failedFilters);
  }, []);

  const handleRetry = (logId) => {
    setRetryState((prev) => ({ ...prev, [logId]: { loading: true, error: null } }));
    invokeApi(
      HTTP_METHODS.POST,
      `${INVOICE_TRACKING_HOSTNAME}${VENDOR_ONBOARD_REST_URLS.BC_LOGS_RETRY}/${logId}`
    )
      .then((res) => {
        if (res.vendorCode) {
          setRetryState((prev) => ({ ...prev, [logId]: { loading: false, error: null } }));
          toast.success('Vendor created successfully');
          fetchSuccessLogs(successFilters);
          fetchFailedLogs(failedFilters);
        } else {
          setRetryState((prev) => ({
            ...prev,
            [logId]: { loading: false, error: res.message || 'Retry failed' },
          }));
        }
      })
      .catch(() => {
        setRetryState((prev) => ({
          ...prev,
          [logId]: { loading: false, error: 'Network error. Please try again.' },
        }));
      });
  };

  const handleManualCode = (logId) => {
    setManualCodeModal({ open: true, logId });
    setManualCode('');
  };

  const submitManualCode = () => {
    invokeApi(
      HTTP_METHODS.POST,
      `${INVOICE_TRACKING_HOSTNAME}${VENDOR_ONBOARD_REST_URLS.BC_LOGS_MANUAL_CODE}/${manualCodeModal.logId}`,
      { vendorCode: manualCode }
    )
      .then((res) => {
        if (res.vendorCode) {
          toast.success('Vendor code saved successfully');
          setManualCodeModal({ open: false, logId: null });
          fetchSuccessLogs(successFilters);
          fetchFailedLogs(failedFilters);
        } else {
          toast.error(res.message || 'Failed to save vendor code');
        }
      })
      .catch(() => {
        toast.error('Network error. Please try again.');
      });
  };

  const successColumns = [
    { label: 'Vendor Name', id: 'vendorName', render: (row) => row.vendorName || '-' },
    { label: 'Form ID', id: 'formId', render: (row) => row.vendorForm?.id ?? row.vendorForm ?? '-' },
    { label: 'Vendor Code', id: 'vendorCode', render: (row) => row.vendorForm?.vendorCode || '-' },
    { label: 'Timestamp', id: 'requestedAt', render: (row) => formatDate(row.requestedAt) },
  ];

  const failedColumns = [
    { label: 'Vendor Name', id: 'vendorName', render: (row) => row.vendorName || '-' },
    { label: 'Form ID', id: 'formId', render: (row) => row.vendorForm?.id ?? row.vendorForm ?? '-' },
    { label: 'Vendor Code', id: 'vendorCode', render: () => '-' },
    { label: 'Timestamp', id: 'requestedAt', render: (row) => formatDate(row.requestedAt) },
    { 
      label: 'Reason', 
      id: 'reason', 
      render: (row) => {
      const reason = row.errorMessage || '-';
      return (
        <span style={{
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          lineHeight: '1.6',
          textAlign: 'center',
          display: 'block',
          maxWidth: '350px',
          margin: '0 auto'
        }}>
          {reason}
        </span>
      );
    }
    },
    {
      label: 'Action',
      id: 'action',
      render: (row) => {
        const rowId = row.id ?? row._id;
        const state = retryState[rowId] || {};
        const isDuplicate = 
          row.errorMessage?.toLowerCase().includes('already exists') &&
          (row.errorMessage?.toLowerCase().includes('gst') || 
          row.errorMessage?.toLowerCase().includes('pan'));
        return (         
          <div>
            {isDuplicate ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleManualCode(rowId)}
              >
                Enter Vendor Code Manually
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                disabled={state.loading}
                onClick={() => handleRetry(rowId)}
                startIcon={state.loading ? <CircularProgress size={14} /> : null}
              >
                {state.loading ? 'Retrying...' : 'Retry'}
              </Button>
            )}
            {state.error && (
              <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                {state.error}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)}>
        <Tab label="Successful Requests" />
        <Tab label="Failed Requests" />
      </Tabs>
      <TabPanel value={subTab} index={0}>
        <MuiTable
          columnsList={successColumns}
          dataList={successLogs.results || []}
          filters={successFilters}
          pageCount={successLogs.totalPages}
          onChange={(page) => {
            const f = { ...successFilters, page };
            setSuccessFilters(f);
            fetchSuccessLogs(f);
          }}
        />
      </TabPanel>
      <TabPanel value={subTab} index={1}>
        <MuiTable
          columnsList={failedColumns}
          dataList={failedLogs.results || []}
          filters={failedFilters}
          pageCount={failedLogs.totalPages}
          onChange={(page) => {
            const f = { ...failedFilters, page };
            setFailedFilters(f);
            fetchFailedLogs(f);
          }}
        />
      </TabPanel>

      <Dialog open={manualCodeModal.open} onClose={() => setManualCodeModal({ open: false, logId: null })}>
        <DialogTitle>Enter Vendor Code Manually</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Vendor Code"
            fullWidth
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            style={{ marginTop: '8px' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualCodeModal({ open: false, logId: null })}>Cancel</Button>
          <Button variant="contained" onClick={submitManualCode} disabled={!manualCode}>Submit</Button>
        </DialogActions>
      </Dialog> 

    </div>
  );
};

export default VendorIntegrations;
